export enum UserRole {
  ADMIN = 'ADMIN',
  SUPERVISOR = 'SUPERVISOR',
  AGENT = 'AGENT',
  VIEWER = 'VIEWER',
  LL = 'LL',
}

export interface User {
  id: string;
  username: string;
  password?: string;
  email?: string;
  role: UserRole;
  authCode: string;
  pin?: string;
  otpSecret?: string;
  otpEnabled?: boolean;
  pushSubscription?: PushSubscriptionJSON;
}

export enum Tab {
  FlightDashboard = 'Flight Dashboard',
  CrewMovement = 'Crew Movement',
  CounterDistribution = 'Counter Distribution',
  Security = 'Security',
  Reports = 'Reports',
  Admin = 'Admin Panel',
  ArrivalDashboard = 'Arrival Dashboard',
}

export const STATIONS = [
  'Crew Arrived',
  '1st X-ray',
  'Immigration In',
  'Immigration Out',
  'Duty Free',
  'At Gate',
  'At Aircraft',
  'Ready For Boarding',
  '1st Pax IN',
  'Last Pax',
  'Doors Closed',
  'Push Back',
] as const;

export type Station = typeof STATIONS[number];

export const ARRIVAL_STATIONS = [
  'Aircraft Arrival',
  'Aircraft Doors Opened',
  '1st Pax Disembark',
  'Last Pax Disembark',
  'Crew Disembark',
  'Arrival Closed',
] as const;

export type ArrivalStation = typeof ARRIVAL_STATIONS[number];


export interface ATSRData {
  [key: string]: string;
  'ATSR NO': string;
  'Excess Baggage': string;
  'Heavy Weight': string;
  'AUP': string;
  'Notes': string;
}

export interface TimestampEntry {
  time: string; // ISO string
  modified: boolean;
  by: string; // username
}

export type CrewMovementData = {
  [key in Station]?: {
    crew: TimestampEntry[];
    pilots: TimestampEntry[];
  };
};

export type ArrivalMovementData = {
  [key in ArrivalStation]?: {
    events: TimestampEntry[];
  };
} & {
  wheelchairs: string;
};

export interface CounterDistributionData {
  etd: string;
  flightNo: string;
  booking: {
    c: string;
    m: string;
    inf: string;
    totalPassengers: string;
  };
  counters: { [key: string]: string };
  gates: { [key: string]: string };
  finalFigures: {
    c: string;
    m: string;
    inf: string;
    totalPassengers: string;
    'Total Bags': string;
    'Crew Bags': string;
    'Waved Bags': string;
    'Wheel Chairs': string;
    'Buggy': string;
    'Final Fuel': string;
  };
}

export interface ScannedPassenger {
  sequence: number;
  name: string;
  seatNumber: string;
  note: string;
  scannedAt: string; // ISO string
  scannedBy: string; // username
}

export interface NotificationData {
  station: Station | ArrivalStation;
  time: string; // The time string to display
  timestamp: number; // A unique timestamp (e.g., Date.now()) to identify the notification
  triggeredBy: string;
  soundType?: 'arrivalClosed';
}

export interface FlightData {
  crewMovement: CrewMovementData;
  counterDistribution: CounterDistributionData;
  atsr: ATSRData;
  immigrationApproved: boolean;
  immigrationComment: string;
  crewAndPilotsArrivedTogether: boolean;
  capturedDocs: { url: string; capturedAt: string; by: string }[];
  newsFeed?: { [id: string]: Omit<NewsMessage, 'id'> };
  scannedPassengers?: ScannedPassenger[];
  lastNotification?: NotificationData | null;
  finalizeTimestamp?: TimestampEntry | null;
  isReturnFlight?: boolean;
  arrivalMovement?: ArrivalMovementData;
}

export const initialFlightData: FlightData = {
  crewMovement: {},
  counterDistribution: {
    etd: '',
    flightNo: '',
    booking: {
      c: '',
      m: '',
      inf: '',
      totalPassengers: '0',
    },
    counters: {
      'Counter 1': '', 'Counter 2': '', 'Counter 3': '', 'Counter 4': '', 
      'Counter 5': '', 'Counter 6': '', 'Counter 7': '', 'Counter 8 (Excess)': ''
    },
    gates: {
      'Immigration': '', 'Gate Control': '', 'System-Visa Check': '', 
      'Crew 1 (Crew)': '', 'Crew 2 (Pilots)': '', 'Que Control': '', 'Jetty': '', 'Custom': ''
    },
    finalFigures: {
      c: '',
      m: '',
      inf: '',
      totalPassengers: '0',
      'Total Bags': '', 
      'Crew Bags': '', 
      'Waved Bags': '', 
      'Wheel Chairs': '', 
      'Buggy': '',
      'Final Fuel': ''
    }
  },
  atsr: {
    'ATSR NO': '',
    'Excess Baggage': '',
    'Heavy Weight': '',
    'AUP': '',
    'Notes': ''
  },
  immigrationApproved: false,
  immigrationComment: '',
  crewAndPilotsArrivedTogether: false,
  capturedDocs: [],
  newsFeed: {},
  scannedPassengers: [],
  lastNotification: null,
  finalizeTimestamp: null,
  isReturnFlight: false,
  arrivalMovement: {
    wheelchairs: '',
  },
};

export interface FlightRecord extends FlightData {
  id: string; // flightNo-date
  flightDate: string;
}

export interface NewsMessage {
  id: string;
  text: string;
  sender: string;
  timestamp: number;
}

export interface LoginHistoryLog {
  userId: string;
  username: string;
  action: 'login' | 'logout';
  timestamp: number;
  ip?: string;
  city?: string;
  region?: string;
  countryName?: string;
}

export interface SessionHistoryLog {
  key: string;
  userId: string;
  username: string;
  timestamp: number;
}
